import type { DOMAttributes } from "@react-types/shared";
import type { ReactElement, ReactNode } from "react";
import {
  Focusable,
  Tooltip as RATooltip,
  TooltipTrigger as RATooltipTrigger,
} from "react-aria-components";
import { tv } from "tailwind-variants";

import { TooltipAttachedContext } from "./tooltip-attached-context";

type TooltipPlacement =
  | "top"
  | "bottom"
  | "left"
  | "right"
  | "top start"
  | "top end"
  | "bottom start"
  | "bottom end"
  | "left top"
  | "left bottom"
  | "right top"
  | "right bottom";

const tooltipStyles = tv({
  base: "z-50 rounded-lg",
  variants: {
    variant: {
      /** Compact text tooltip: the default */
      default: "bg-background px-2 py-1 text-sm text-text-primary",
      /** Rich card tooltip with border + background */
      card: "w-64 border border-border bg-background-2 p-4 shadow-lg",
      /** No chrome: content supplies its own styling */
      unstyled:
        "z-20 border-0 bg-transparent p-0 shadow-none backdrop-blur-none",
    },
  },
  defaultVariants: { variant: "default" },
});

interface CustomTooltipProps {
  children: ReactElement<DOMAttributes, string>;
  content?: ReactNode;
  delay?: number;
  placement?: TooltipPlacement;
  offset?: number;
  containerPadding?: number;
  variant?: "default" | "card" | "unstyled";
  className?: string;
}

export function CustomTooltip({
  children,
  content,
  delay = 300,
  placement,
  offset,
  containerPadding,
  variant = "default",
  className,
}: Readonly<CustomTooltipProps>) {
  if (content == null) return children;

  return (
    <RATooltipTrigger delay={delay}>
      <TooltipAttachedContext value={true}>
        <Focusable>{children}</Focusable>
      </TooltipAttachedContext>
      <RATooltip
        className={tooltipStyles({ variant, class: className })}
        placement={placement}
        offset={offset}
        containerPadding={containerPadding}
      >
        {content}
      </RATooltip>
    </RATooltipTrigger>
  );
}
